import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  /** Zero-based page index. */
  page: number
  pageSize: number
  totalElements: number
  totalPages: number
  /** Size choices shown in the selector. Pass an empty array to hide the selector. */
  pageSizeOptions?: number[]
  /** Disables controls during page transitions (e.g. when `isPlaceholderData` is true). */
  loading?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  /** Rendered next to the range label — optional trailing content (e.g. a "Total: 47 active" badge). */
  leadingSlot?: React.ReactNode
}

/** Elided page-number sequence: always shows first/last, current ± 1, with ellipses. */
function pageWindow(current: number, total: number): (number | 'dots')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i)
  const out: (number | 'dots')[] = [0]
  const start = Math.max(1, current - 1)
  const end = Math.min(total - 2, current + 1)
  if (start > 1) out.push('dots')
  for (let i = start; i <= end; i++) out.push(i)
  if (end < total - 2) out.push('dots')
  out.push(total - 1)
  return out
}

export default function Pagination({
  page, pageSize, totalElements, totalPages,
  pageSizeOptions = [10, 25, 50],
  loading = false,
  onPageChange,
  onPageSizeChange,
  leadingSlot,
}: PaginationProps) {
  if (totalElements === 0) return null

  const rangeStart = page * pageSize + 1
  const rangeEnd = Math.min((page + 1) * pageSize, totalElements)
  const canPrev = page > 0 && !loading
  const canNext = page < totalPages - 1 && !loading
  const showSelector = pageSizeOptions.length > 0 && onPageSizeChange
  // One page with fewer rows than the smallest size option and no leading slot — no controls needed.
  if (totalPages <= 1 && !showSelector && !leadingSlot) return null

  const btn = 'min-w-[32px] h-8 px-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
  const btnIdle = 'text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800'
  const btnActive = 'bg-brand-500 text-white hover:bg-brand-600'

  return (
    <div className="flex items-center justify-between gap-3 pt-3 mt-1 border-t border-gray-100 dark:border-gray-800">
      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span>Showing <span className="font-medium text-gray-700 dark:text-gray-300">{rangeStart}–{rangeEnd}</span> of <span className="font-medium text-gray-700 dark:text-gray-300">{totalElements}</span></span>
        {leadingSlot}
      </div>

      <div className="flex items-center gap-2">
        {showSelector && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={loading}
            className="h-8 px-2 text-xs rounded-lg border border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
            aria-label="Rows per page">
            {pageSizeOptions.map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        )}

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => onPageChange(page - 1)} disabled={!canPrev}
              className={`${btn} ${btnIdle} flex items-center gap-0.5`} aria-label="Previous page">
              <ChevronLeft size={14} />
            </button>
            {pageWindow(page, totalPages).map((p, i) => p === 'dots' ? (
              <span key={`d-${i}`} className="px-1 text-xs text-gray-400 select-none">…</span>
            ) : (
              <button key={p} type="button" onClick={() => onPageChange(p)} disabled={loading}
                className={`${btn} ${p === page ? btnActive : btnIdle}`}
                aria-current={p === page ? 'page' : undefined}
                aria-label={`Page ${p + 1}`}>
                {p + 1}
              </button>
            ))}
            <button type="button" onClick={() => onPageChange(page + 1)} disabled={!canNext}
              className={`${btn} ${btnIdle} flex items-center gap-0.5`} aria-label="Next page">
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
